import React, { useEffect, useMemo, useState } from 'react';
import type { ClassMemberGroupMeta } from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import type { FlatMember } from './GeneralAssignmentSettingsModal';
import NoteGroupSelector from './NoteGroupSelector';

export type MemberPickGroup = {
  groupId: string;
  groupName: string;
  members: FlatMember[];
};

export function groupFlatMembers(members: FlatMember[]): MemberPickGroup[] {
  const order: string[] = [];
  const byId = new Map<string, MemberPickGroup>();
  for (const m of members) {
    const groupId = m.groupId || '__ungrouped__';
    const groupName = m.groupName || '그룹';
    let g = byId.get(groupId);
    if (!g) {
      g = { groupId, groupName, members: [] };
      byId.set(groupId, g);
      order.push(groupId);
    }
    g.members.push(m);
  }
  return order.map((id) => byId.get(id)!);
}

type Props = {
  members: FlatMember[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  /** 모달 열릴 때 기본으로 보여줄 그룹 */
  initialGroupId?: string | null;
  /** 에듀코디 과제 생성 모달과 동일한 레이아웃 */
  educodiLayout?: boolean;
};

const AssignmentMemberPickField: React.FC<Props> = ({
  members,
  selectedIds,
  onChange,
  initialGroupId = null,
  educodiLayout = false,
}) => {
  const groups = useMemo(() => groupFlatMembers(members), [members]);
  const groupMetas: ClassMemberGroupMeta[] = useMemo(
    () =>
      groups.map((g, i) => ({
        id: g.groupId,
        name: g.groupName,
        sortOrder: i,
      })),
    [groups]
  );

  const [pickGroupId, setPickGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (!groups.length) {
      setPickGroupId(null);
      return;
    }
    const preferred =
      (initialGroupId && groups.some((g) => g.groupId === initialGroupId)
        ? initialGroupId
        : null) ??
      groups.find((g) => g.members.some((m) => selectedIds.has(m.id)))?.groupId ??
      groups[0].groupId;
    setPickGroupId(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedIds는 초기 탭 힌트용
  }, [groups, initialGroupId, members]);

  const activeGroup = groups.find((g) => g.groupId === pickGroupId) ?? groups[0] ?? null;
  const visibleMembers = activeGroup?.members ?? [];
  const groupAllOn =
    visibleMembers.length > 0 && visibleMembers.every((m) => selectedIds.has(m.id));

  const toggleId = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleVisibleGroup = () => {
    const ids = visibleMembers.map((m) => m.id);
    const next = new Set(selectedIds);
    if (groupAllOn) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    onChange(next);
  };

  if (educodiLayout) {
    return (
      <div className="cp-apn-create-members">
        <div className="cp-apn-create-members-header">
          <span>과제를 부여할 멤버 선택</span>
          <button
            type="button"
            className="cp-apn-create-select-all"
            disabled={visibleMembers.length === 0}
            onClick={toggleVisibleGroup}
          >
            {groupAllOn ? '전체 해제' : '전체 선택'}
          </button>
        </div>

        {groups.length > 1 ? (
          <div className="cp-apn-create-group-tabs">
            {groups.map((g) => (
              <button
                key={g.groupId}
                type="button"
                className={`cp-apn-create-group-tab${
                  pickGroupId === g.groupId ? ' is-active' : ''
                }`}
                onClick={() => setPickGroupId(g.groupId)}
              >
                {g.groupName}
              </button>
            ))}
          </div>
        ) : null}

        {members.length === 0 ? (
          <p className="cp-apn-empty-hint">등록된 멤버가 없습니다.</p>
        ) : visibleMembers.length === 0 ? (
          <p className="cp-apn-empty-hint">이 그룹에 멤버가 없습니다.</p>
        ) : (
          <div className="cp-apn-create-students-grid">
            {visibleMembers.map((m) => {
              const on = selectedIds.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`cp-apn-create-student${on ? ' is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleId(m.id)}
                >
                  <span className="num">{String(m.orderIndex).padStart(2, '0')}</span>
                  <span className="name">{displayClassMemberName(m).trim() || '(이름 없음)'}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const selectedCount = selectedIds.size;

  return (
    <div className="cp-apn-field">
      <div className="cp-apn-field-row">
        <span>
          배정 멤버
          {selectedCount > 0 ? (
            <span className="cp-apn-member-pick-count"> ({selectedCount}명)</span>
          ) : null}
        </span>
        <button
          type="button"
          className="cp-apn-text-btn"
          disabled={visibleMembers.length === 0}
          onClick={toggleVisibleGroup}
        >
          {groupAllOn ? '이 그룹 해제' : '이 그룹 선택'}
        </button>
      </div>

      {groups.length > 1 ? (
        <div className="cp-apn-member-pick-tabs">
          <NoteGroupSelector
            groups={groupMetas}
            selectedGroupId={pickGroupId}
            onChange={setPickGroupId}
            preferSelectWhenOver={8}
          />
        </div>
      ) : null}

      {members.length === 0 ? (
        <p className="cp-apn-empty-hint">등록된 멤버가 없습니다.</p>
      ) : visibleMembers.length === 0 ? (
        <p className="cp-apn-empty-hint">이 그룹에 멤버가 없습니다.</p>
      ) : (
        <div className="cp-apn-member-pick-grid">
          {visibleMembers.map((m) => {
            const on = selectedIds.has(m.id);
            return (
              <button
                key={m.id}
                type="button"
                className={`cp-apn-member-chip${on ? ' is-on' : ''}`}
                onClick={() => toggleId(m.id)}
              >
                {m.orderIndex}. {displayClassMemberName(m).trim() || '(이름 없음)'}
              </button>
            );
          })}
        </div>
      )}

      {groups.length > 1 ? (
        <p className="cp-apn-empty-hint cp-apn-member-pick-hint">
          다른 그룹 멤버를 선택하면 해당 그룹에도 과제가 부여됩니다.
        </p>
      ) : null}
    </div>
  );
};

export default AssignmentMemberPickField;
